<template>
    <form class="form-builder__form vfb-form" :class="{ 'js-vfb-ajax-form': ajax }"
          :data-ajax="ajax ? 'true' : null"
          ref="form" :data-action="action" method="POST" enctype="multipart/form-data">
        <slot></slot>
    </form>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import type { PropType } from 'vue';
import { FormValidator } from '@elements/form-validation/dist/core/index.js';
import { handleAjaxHashCash, armFormGuardListener } from '../../utils/hash-cash';
import { resolveValidationMessages } from '../../utils/validation-messages';
import { customValidators } from '../../utils/custom-validators';

const props = defineProps({
    action: String,
    validationMessages: {
        type: Object,
        default: null
    },
    validationMode: {
        type: String as PropType<'always' | 'after-submit' | 'submit-only'>,
        default: 'always'
    },
    fields: {
        type: Object,
        default: undefined
    },
    trigger: {
        type: Object,
        default: undefined
    },
    locale: {
        type: String,
        default: undefined
    },
    ajax: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['form-submit', 'hashcash-success', 'ajax-success', 'ajax-error', 'submit-aborted']);

const form = ref<HTMLFormElement | null>(null);
const fv = ref<InstanceType<typeof FormValidator> | null>(null);

// True zwischen einem validen Submit und dem Ende der Absendung. Dient als
// Re-Entry-Guard: ein disabled Button fängt nur den Klick ab, Enter im
// Textfeld und form.requestSubmit() lösen weiterhin ein submit-Event aus.
const submitting = ref(false);

const submitButtonsOf = (formEl: HTMLFormElement) =>
    formEl.querySelectorAll('button[type="submit"], button:not([type])');

// Absendung beendet, ohne dass die Seite navigiert oder das Formular ersetzt
// wurde (Abbruch oder Server-Fehler) → Formular wieder bedienbar machen.
const releaseSubmitLock = () => {
    submitting.value = false;
    const formEl = form.value;
    if (formEl) {
        submitButtonsOf(formEl).forEach(el => el.removeAttribute('disabled'));
    }
};

// Named so it can be detached again — the listener sits on `window` and would
// otherwise outlive the component (several forms per page unmount independently).
const onPageShow = (evt: Event) => {
    if ((evt as PageTransitionEvent).persisted) {
        releaseSubmitLock();
    }
};

function warnDeprecatedProps() {
    if (props.fields !== undefined) {
        console.warn('[vue-form-builder] The "fields" prop is ignored since form-validation v3 — validation rules are defined declaratively via HTML attributes (required, minlength, pattern, data-validation-rules, …).');
    }
    if (props.trigger !== undefined) {
        console.warn('[vue-form-builder] The "trigger" prop is ignored since form-validation v3 — use the "validationMode" prop instead (\'always\' | \'after-submit\' | \'submit-only\'). The default \'always\' matches the previous trigger { event: \'input\' }.');
    }
    if (props.locale !== undefined) {
        console.warn('[vue-form-builder] The "locale" prop is ignored — pass translated messages via the "validationMessages" prop (fallback is English).');
    }
}

onMounted(() => {
    const formEl = form.value;
    if (!formEl) return;

    warnDeprecatedProps();

    // Listen for HashCash success and re-emit as Vue event
    formEl.addEventListener('success.hash-cash', () => {
        emit('hashcash-success');
    });
    // Ajax submit results (from submitFormAjax) — re-emit as Vue events so the
    // FormBuilder can render the server HTML (success snippet / error summary).
    formEl.addEventListener('vfb:ajax-success', (e) => {
        emit('ajax-success', (e as CustomEvent<{ html?: string }>).detail?.html ?? '');
    });
    formEl.addEventListener('vfb:ajax-error', (e) => {
        emit('ajax-error', (e as CustomEvent<{ html?: string }>).detail?.html ?? '');
    });
    // Submission aborted before any server response (failed proof-of-work or a
    // cancelled form-guard) — let the parent drop the loading overlay again.
    formEl.addEventListener('failed.hash-cash', () => {
        releaseSubmitLock();
        emit('submit-aborted');
    });
    formEl.addEventListener('form-guard.cancel', () => {
        releaseSubmitLock();
        emit('submit-aborted');
    });
    // Server rejected the submission (validation errors): the form stays on the
    // page, so it has to become submittable again. On vfb:ajax-success the form
    // is hidden instead — the lock deliberately stays.
    formEl.addEventListener('vfb:ajax-error', () => releaseSubmitLock());
    // Back-navigation restoring the page from the bfcache after a classic
    // submit: no submission is in flight any more, release the lock.
    window.addEventListener('pageshow', onPageShow);
    // Track first user interaction for the form-guard gate (bot protection).
    armFormGuardListener(formEl);

    formEl.addEventListener('submit', () => {
        setTimeout(() => {
            formEl.querySelector('.is-invalid')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
    }, { capture: true });

    fv.value = new FormValidator(formEl, {
        base: '.js-form-validation',
        item: '.js-form-validation__group',
        errors: '.js-form-validation__errors',
    }, {
        validClass: 'is-valid',
        invalidClass: 'is-invalid',
        validationMode: props.validationMode,
        messages: resolveValidationMessages(props.validationMessages as Record<string, string> | null),
        customValidators,
    });

    formEl.addEventListener('submit', (evt: Event) => {
        evt.preventDefault();
        // A submission is already in flight — drop this one instead of starting
        // a second hash-cash round (and a second POST).
        if (submitting.value) {
            return;
        }
        submitting.value = true;
        emit('form-submit');
        // Declarative locking happens via the SubmitButton's `disabled` prop;
        // this stays as a fallback for slot buttons that do not bind it.
        submitButtonsOf(formEl).forEach((el) => {
            el.setAttribute('disabled', '');
        });
        handleAjaxHashCash(formEl);
    });
});

onUnmounted(() => {
    window.removeEventListener('pageshow', onPageShow);
});

// this way we can use the FormValidator instance from this component's parent
defineExpose({
    fv,
    submitting
})
</script>
